import { DialogTitle } from "@radix-ui/react-dialog";
import { memo, useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog/dialog";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";

import { useHandleFormState } from "./hooks";

type Props = {
	// もしもキャンセルした場合の料金
	willCancelPrice?: number;
	//
	onUpdateReservationDetailCancel: (canceledPrice: number) => Promise<void>;
};

export const ReservationDetailCancelModal = memo(({ willCancelPrice, onUpdateReservationDetailCancel }: Props) => {
	const {
		canceledPrice,
		canceledPriceErrorMessage,
		hasError,
		//
		handleChangeCanceledPrice,
	} = useHandleFormState(willCancelPrice);

	const [canOpenModal, setCanOpenModal] = useState(false);
	/**
	 * モーダルを表示
	 */
	const handleClickOpenModal = useCallback(() => {
		setCanOpenModal(true);
	}, []);
	/**
	 * モーダルを非表示
	 */
	const handleClickCloseModal = useCallback(() => {
		setCanOpenModal(false);
	}, []);
	/**
	 * モーダルの表示・非表示を制御
	 */
	const onChangeCanOpenModal = useCallback((open: boolean) => {
		setCanOpenModal(open);
	}, []);
	/**
	 * モーダルの表示・非表示を制御
	 */
	const handleOpenChange = useCallback((open: boolean) => {
		setCanOpenModal(open);
	}, []);

	/**
	 * 予約詳細をキャンセルし、キャンセル料金を設定する
	 */
	const handleClickUpdateReservationDetailCancel = useCallback(() => {
		if (hasError || canceledPrice === undefined) return;

		onUpdateReservationDetailCancel(canceledPrice).then(() => onChangeCanOpenModal(false));
	}, [canceledPrice, hasError, onChangeCanOpenModal, onUpdateReservationDetailCancel]);

	const hasWillCancelPrice = useMemo(() => willCancelPrice !== undefined, [willCancelPrice]);
	const willCancelPriceText = useMemo(() => {
		return `${willCancelPrice ? willCancelPrice.toLocaleString() : "未設定"}`;
	}, [willCancelPrice]);

	/**
	 * キャンセル可能料金が含まれていなければ、disabled の条件に含めない
	 */
	const disabled = useMemo(
		() => hasError || (willCancelPrice !== undefined && (canceledPrice ?? 0) > willCancelPrice),
		[canceledPrice, hasError, willCancelPrice],
	);

	/**
	 * モーダルを表示するボタン
	 */
	if (!canOpenModal) {
		return (
			<Button variant="outline" type="button" size="sm" onClick={handleClickOpenModal}>
				キャンセルを行う
			</Button>
		);
	}

	return (
		<Dialog open={canOpenModal} onOpenChange={handleOpenChange}>
			<DialogContent className="">
				<DialogHeader className="border-b border-b-zinc-200 pb-4">
					<DialogTitle>予約のキャンセル</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 p-2">
					<div>
						<p className="text-sm">
							徴収できる最大のキャンセル料金は
							<span className="text-2xl underline">{willCancelPriceText}</span>
							{hasWillCancelPrice ? "円" : ""}
							です。
						</p>
					</div>

					<div>
						<div className="flex items-end gap-x-3">
							<div className="min-w-60 max-w-60">
								<InputNumberField
									label="徴収するキャンセル料金"
									value={canceledPrice}
									handleInput={handleChangeCanceledPrice}
									errorMessage={canceledPriceErrorMessage}
								/>
							</div>

							<span className="text-sm font-semibold">円</span>
						</div>
					</div>
				</div>

				<div className="flex items-center justify-end gap-x-3 border-t border-t-zinc-200 px-6 pt-4">
					<Button variant="outline" type="button" onClick={handleClickCloseModal}>
						戻る
					</Button>

					<Button type="button" disabled={disabled} onClick={handleClickUpdateReservationDetailCancel}>
						実行する
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
});
